import { useTheme } from "@/hooks/useTheme";

type Partner = { name: string; src?: string; objectFit?: "cover" | "contain" };

const buildPartners = (isDark: boolean): Partner[] => [
  { name: "Raymond Legal", src: isDark ? "/partner-raymondlegal.png" : "/partner-raymondlegal-light.png", objectFit: "contain" },
  { name: "ProActive Legal", src: "/partner-proactive.svg", objectFit: "contain" },
  { name: "Ratner" },
  { name: "Turpin Miller", src: "/client-turpinmiller.jpg", objectFit: "contain" },
  { name: "Visa Positive", src: "/client-visapositive.jpg", objectFit: "contain" },
  { name: "Tann Law", src: "/client-tannlaw.png", objectFit: "contain" },
];

const FileIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const DiamondIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const findings: { severity: "critical" | "warning"; text: string }[] = [
  { severity: "critical", text: "Disclosure missing on slide 4" },
  { severity: "critical", text: "Performance claim lacks methodology" },
  { severity: "warning", text: "Risk warning font size below 8pt" },
];

const MetaroomMockup = () => (
  <div className="flex-1 min-h-0 rounded-sm border border-[var(--color-border)] overflow-hidden bg-[var(--color-bg)] flex flex-col">
    <div className="flex-1 flex flex-col gap-3 p-4 min-h-0 overflow-hidden justify-center">
      {/* User message — right aligned */}
      <div className="flex items-start gap-1.5 justify-end">
        <div className="flex flex-col items-end gap-1 max-w-[80%]">
          <div className="flex items-center gap-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-1.5 py-0.5 text-[var(--color-fg-tertiary)]">
            <FileIcon />
            <span className="text-[8px]">Asia_Focus_VCC_Q1.pdf</span>
          </div>
          <div className="bg-[#818cf8]/15 border border-[#818cf8]/40 rounded px-2 py-1 text-[9px] text-[var(--color-fg)] leading-snug">
            Review for MAS advertising compliance
          </div>
        </div>
        <div className="w-[18px] h-[18px] rounded-full bg-[#818cf8] flex items-center justify-center text-white text-[8px] font-bold shrink-0">
          J
        </div>
      </div>

      {/* Agent thinking */}
      <div className="flex items-center gap-1.5">
        <div className="w-[18px] h-[18px] rounded-full bg-[#10b981] flex items-center justify-center shrink-0">
          <DiamondIcon />
        </div>
        <div className="flex items-center gap-1 text-[9px] text-[var(--color-fg-tertiary)] italic">
          <SpinnerIcon />
          Reviewing against MAS Notice 04 / FAA-N16...
        </div>
      </div>

      {/* Agent response */}
      <div className="flex items-start gap-1.5">
        <div className="w-[18px] shrink-0" />
        <div className="flex flex-col gap-1 max-w-[88%]">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2 py-1 text-[9px] text-[var(--color-fg)] leading-snug">
            Found <strong className="font-semibold">3 critical</strong>, <strong className="font-semibold">2 warnings</strong>, 8 passed.
          </div>
          <div className="flex flex-col gap-0.5 pl-1.5">
            {findings.map((f) => (
              <div key={f.text} className="flex items-center gap-1.5 text-[8px]">
                <span
                  className="w-1 h-1 rounded-full shrink-0"
                  style={{ background: f.severity === "critical" ? "#ef4444" : "#f59e0b" }}
                />
                <span className="text-[var(--color-fg-secondary)]">{f.text}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-1.5 py-0.5 self-start mt-0.5 text-[var(--color-fg-tertiary)]">
            <FileIcon />
            <span className="text-[8px]">Compliance_Review.pdf</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const stats = [
  { value: "15", label: "Clients" },
  { value: "3", label: "Countries" },
  { value: "200+", label: "Cases" },
  { value: "3 hrs → mins", label: "Per case" },
];

export function V3ProductsSlide({ isActive: _ }: { isActive: boolean }) {
  const { theme } = useTheme();
  const partners = buildPartners(theme === "dark");

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[28px] font-light tracking-tight mb-3 font-[family-name:var(--font-heading)]">
        Real agents. Real clients. Real data.
      </h2>

      <div className="flex-1 min-h-0 flex flex-col gap-3 max-h-[360px]">
        {/* Top row — Two large product views */}
        <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 max-h-[230px]">
          <div className="flex flex-col min-h-0">
            <div className="mb-1.5">
              <div className="text-[10px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] text-[var(--color-fg)]">
                AI Paralegal Agent
              </div>
              <div className="text-[9px] font-[family-name:var(--font-mono)] uppercase tracking-wider text-[var(--color-fg-tertiary)] mt-0.5">
                Deployed at Raymond Legal, ProActive Legal · 15 firms total
              </div>
            </div>
            <div className="flex-1 min-h-0 rounded-sm border border-[var(--color-border)] overflow-hidden">
              <img src="/product-xeni.png" alt="AI paralegal agent screenshot" className="w-full h-full object-cover object-left-top" />
            </div>
          </div>

          <div className="flex flex-col min-h-0">
            <div className="mb-1.5">
              <div className="text-[10px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] text-[var(--color-fg)]">
                AI Compliance Agent
              </div>
              <div className="text-[9px] font-[family-name:var(--font-mono)] uppercase tracking-wider text-[var(--color-fg-tertiary)] mt-0.5">
                Deployed at Ratner · First module of fund ops & compliance suite
              </div>
            </div>
            <MetaroomMockup />
          </div>
        </div>

        {/* Bottom row — stats (left) + partner logos (right) */}
        <div className="grid grid-cols-[2fr_3fr] gap-4 shrink-0">
          {/* Stats */}
          <div className="flex flex-col">
            <div className="text-[10px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] text-[var(--color-fg)] mb-1.5">
              Track record
            </div>
            <div className="flex-1 rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] grid grid-cols-2 gap-x-3 gap-y-2 px-3 py-2.5">
              {stats.map((stat) => (
                <div key={stat.label} className="flex items-baseline gap-1.5 whitespace-nowrap">
                  <span className="text-[16px] font-light tracking-tight font-[family-name:var(--font-heading)] leading-none">
                    {stat.value}
                  </span>
                  <span className="text-[8px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Logos */}
          <div className="flex flex-col">
            <div className="text-[10px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] text-[var(--color-fg)] mb-1.5">
              Partners &amp; Clients
            </div>
            <div className="flex-1 rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] flex items-center justify-around gap-3 px-4">
              {partners.map((p) =>
                p.src ? (
                  <img
                    key={p.name}
                    src={p.src}
                    alt={p.name}
                    title={p.name}
                    className="h-[28px] w-auto opacity-80"
                    style={{ objectFit: p.objectFit ?? "contain" }}
                  />
                ) : (
                  <span key={p.name} className="text-[10px] font-[family-name:var(--font-mono)] font-medium text-[var(--color-fg-secondary)] uppercase tracking-[0.08em] whitespace-nowrap">
                    {p.name}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light">
          Live in three jurisdictions. <strong className="font-medium text-[var(--color-fg)]">Every case feeds the engine.</strong>
        </p>
      </div>
    </div>
  );
}
